import { z } from 'zod';
import {
  EMPTY_FIELD_MESSAGE,
  validateEditorEn,
  validateSpace,
  validateSpaceTextInput,
  validateTextEn,
} from '@Utils/validation';
import { VerifierType, VerifyType } from 'src/Enum/EsgGreenhouse';
import { uploadImageSchema } from '@Containers/UploadImageFormFields/validators';

enum GH_GAS_MGMT_REPORT_MESSAGE {
  INTRO_EN = 'กรุณาระบุคำบรรยายเป็นภาษาอังกฤษ',
  REMARK_EN = 'กรุณาระบุหมายเหตุเป็นภาษาอังกฤษ',
}

enum PF_GH_GAS_MGMT_REPORT_MESSAGE {
  DESCRIPTION_EN = 'กรุณาระบุผลการดำเนินงานและผลลัพธ์ด้านการจัดการก๊าซเรือนกระจกเป็นภาษาอังกฤษ',
  DESCRIPTION = 'กรุณาระบุผลการดำเนินงานและผลลัพธ์ด้านการจัดการก๊าซเรือนกระจก',
  RESULT = 'กรุณาเลือกตัวเลือกผลการดำเนินงานและผลลัพธ์ด้านการจัดการก๊าซเรือนกระจก',
  ASSETS = 'กรุณาเลือกไฟล์ภาพผลการดำเนินงานและผลลัพธ์ด้านการจัดการจัดการก๊าซเรือนกระจก',
  DESCRIPTION_ASSETS_EN = 'กรุณาระบุชื่อรูปภาพ/หัวข้อ/คำอธิบายเพิ่มเติมเป็นภาษาอังกฤษ',
}

enum GH_GAS_EMISSION_VERIFY_MESSAGE {
  VERIFY = 'กรุณาเลือกตัวเลือกการทวนสอบปริมาณการปล่อยก๊าซเรือนกระจกของบริษัท',
  VERIFIER_ID = 'กรุณาระบุรายชื่อหน่วยงานผู้ทวนสอบข้อมูลปริมาณการปล่อยก๊าซเรือนกระจก',
  VERIFIER_OTHER = 'กรุณาระบุชื่อหน่วยงานอื่น ๆ',
  VERIFIER_OTHER_EN = 'กรุณาระบุชื่อหน่วยงานอื่น ๆ เป็นภาษาอังกฤษ',
}

const IUploadImageSchema = uploadImageSchema({
  assets: PF_GH_GAS_MGMT_REPORT_MESSAGE.ASSETS,
  descriptionAssetsEn: PF_GH_GAS_MGMT_REPORT_MESSAGE.DESCRIPTION_ASSETS_EN,
});

export const GhGasEmissionRemarkSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateTextEn, {
      message: GH_GAS_MGMT_REPORT_MESSAGE.REMARK_EN,
    })
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

export const GhGasEmissionVerifySchema = z
  .object({
    remark: z
      .object({
        descriptionTh: z
          .string()
          .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
          .nullable()
          .optional(),
        descriptionEn: z
          .string()
          .regex(validateTextEn, {
            message: GH_GAS_MGMT_REPORT_MESSAGE.REMARK_EN,
          })
          .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
          .nullable()
          .optional(),
      })
      .optional()
      .nullable(),
    verify: z.nativeEnum(VerifyType, {
      message: GH_GAS_EMISSION_VERIFY_MESSAGE.VERIFY,
    }),
    verifier: z
      .object({
        infoCategoryMasterDataId: z.union([z.string(), z.number()]),
        otherNameTh: z.string().optional(),
        otherNameEn: z
          .string()
          .regex(validateEditorEn, {
            message: GH_GAS_EMISSION_VERIFY_MESSAGE.VERIFIER_OTHER_EN,
          })
          .optional(),
      })
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    const { verify, verifier } = data;
    if (
      verify &&
      verify === VerifyType.YES &&
      (verifier === null || verifier?.infoCategoryMasterDataId === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        message: GH_GAS_EMISSION_VERIFY_MESSAGE.VERIFIER_ID,
        path: ['verifier', 'infoCategoryMasterDataId'],
      });
    }

    if (
      verifier?.infoCategoryMasterDataId ===
        VerifierType.GHG_VERIFIER_OTHER_ID &&
      !verifier.otherNameTh
    ) {
      ctx.addIssue({
        code: 'custom',
        message: GH_GAS_EMISSION_VERIFY_MESSAGE.VERIFIER_OTHER,
        path: ['verifier', 'otherNameTh'],
      });
    } else if (verifier?.otherNameTh && !validateSpace(verifier.otherNameTh)) {
      ctx.addIssue({
        code: 'custom',
        message: EMPTY_FIELD_MESSAGE.SPACE_BAR,
        path: ['verifier', 'otherNameTh'],
      });
    }

    if (
      verifier?.infoCategoryMasterDataId ===
        VerifierType.GHG_VERIFIER_OTHER_ID &&
      !verifier.otherNameEn
    ) {
      ctx.addIssue({
        code: 'custom',
        message: GH_GAS_EMISSION_VERIFY_MESSAGE.VERIFIER_OTHER,
        path: ['verifier', 'otherNameEn'],
      });
    } else if (verifier?.otherNameEn && !validateSpace(verifier.otherNameEn)) {
      ctx.addIssue({
        code: 'custom',
        message: EMPTY_FIELD_MESSAGE.SPACE_BAR,
        path: ['verifier', 'otherNameEn'],
      });
    }
  });

export const PerformanceGhGasMgmtSchema = z
  .object({
    result: z
      .nativeEnum(VerifyType, {
        message: PF_GH_GAS_MGMT_REPORT_MESSAGE.RESULT,
      })
      .optional(),
    descriptionTh: z
      .string()
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateEditorEn, {
        message: PF_GH_GAS_MGMT_REPORT_MESSAGE.DESCRIPTION_EN,
      })
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    assets: z.array(IUploadImageSchema).optional(),
    remark: GhGasEmissionRemarkSchema.optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.result === VerifyType.YES) {
      if (!data.descriptionTh) {
        ctx.addIssue({
          code: 'custom',
          message: PF_GH_GAS_MGMT_REPORT_MESSAGE.DESCRIPTION,
          path: ['descriptionTh'],
        });
      }
      if (!data.descriptionEn) {
        ctx.addIssue({
          code: 'custom',
          message: PF_GH_GAS_MGMT_REPORT_MESSAGE.DESCRIPTION,
          path: ['descriptionEn'],
        });
      }
    }
  });

export const GhGasEmissionVolumeSchema = z.object({
  remarkTotalGhGas: GhGasEmissionRemarkSchema,
  remarkScope1: GhGasEmissionRemarkSchema,
  remarkScope2: GhGasEmissionRemarkSchema,
  remarkScope3: GhGasEmissionRemarkSchema,
});

export const PerformanceGhGasMgmtReportSchema = z.object({
  introduction: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateEditorEn, {
        message: GH_GAS_MGMT_REPORT_MESSAGE.INTRO_EN,
      })
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  remark: GhGasEmissionRemarkSchema,
  ghGasEmissionVerify: GhGasEmissionVerifySchema,
  ghGasEmissionVolume: GhGasEmissionVolumeSchema,
  performanceGhGasMgmt: PerformanceGhGasMgmtSchema,
});
